import { type CSSProperties } from "react";
import { ArrowRight, Star } from "lucide-react";
import { Card } from "@/components/ui/card";
import { cursorTip } from "@/components/shared/CursorTip";
import { cn } from "@/lib/utils";

/**
 * 2πr for the r=30 arc in the 72-unit viewBox — an undrawn ring is one whose
 * dash offset is its whole circumference. `ux-ring-draw` in `src/index.css`
 * hardcodes this same number as its `from` (a CSS keyframe cannot read the
 * element's own value), so the two move together.
 */
const RING_LENGTH = 188.5;

/** Panels land one after another rather than as a single slab (motion-lab.html). */
const PANEL_STEP_MS = 200;

/**
 * How long data waits before drawing itself. A card's `panel-in` runs 520ms;
 * its bars and ring start most of the way through, so the reveal reads as the
 * panel arriving *with* its data rather than as two separate events.
 */
const CONTENT_AFTER_PANEL_MS = 280;

/** Per-bar step, used for both the mount reveal and the hover wave. */
const BAR_STEP_MS = 40;

/**
 * Idle offsets for the breathing bars. Without them the chart pumps in unison,
 * which reads as a loading state; offset, it sways. (These are the mockup's
 * nth-child(2n/3n/5n) groups, resolved for the seven fixed columns.)
 */
const BAR_IDLE_SWAY_MS = [0, 500, 1100, 500, 1700, 1100, 0];

/** Bars finish their own reveal before the idle loop may take the transform over. */
const BAR_GROW_MS = 600;

/**
 * The confetti burst: six pieces, each with its own vector and a small delay so
 * the burst reads as a scatter rather than a starburst. Colours come from the
 * app's own tokens and palette.
 *
 * The SET is balanced about the star, and that is what makes the burst look
 * like it comes from the star. The launch point itself has been measured in
 * the running app and is exact — the pieces' rest centre lands on the star
 * glyph's own centre to within a third of a pixel (see `.confetti` in
 * index.css) — so the burst reading as "off to the right" was never the
 * origin: it was these vectors. The previous set sent four of six pieces
 * rightward, including both of the long ones (+78px and +96px against a
 * longest-left of −48px), which put the whole visual mass of the burst on one
 * side of a launch point that was already correct. Moving the origin left to
 * compensate only trades a centred burst that looks skewed for a skewed burst
 * that starts off the star.
 *
 * So: horizontal components sum to about zero (−58, −34, −12, +16, +40, +56),
 * every piece carries the same upward bias a burst has, and each vector is
 * roughly 60px long, so the pieces reach a ragged ring rather than three
 * neighbours and one piece halfway across the card. The irregular pairing of
 * length to angle, and the shuffled delays, are what keep it a scatter.
 */
const CONFETTI = [
  { dx: "-58px", dy: "-24px", delay: "0ms", tone: "bg-[hsl(var(--ring))]" },
  { dx: "-34px", dy: "-50px", delay: "65ms", tone: "bg-amber-500" },
  { dx: "-12px", dy: "-62px", delay: "30ms", tone: "bg-green-600" },
  { dx: "16px", dy: "-60px", delay: "80ms", tone: "bg-[hsl(var(--ring-soft))]" },
  { dx: "40px", dy: "-44px", delay: "15ms", tone: "bg-[hsl(var(--destructive))]" },
  /* The one non-brand fleck is --foreground, the only token that is dark on
     the light card and light on the dark one — the brand navy vanished into
     the dark theme's near-black surface. */
  { dx: "56px", dy: "-20px", delay: "50ms", tone: "bg-foreground" },
];

interface InsightCardsProps {
  /** Seven weeks of cleared counts, oldest first; the last entry is this week. */
  weeklyCleared: number[];
  clearedThisWeek: number;
  /** Best of the six weeks before this one — what "best in 7 weeks" is measured against. */
  bestPastWeek: number;
  /** Tallest column in the set, so the bars scale against a real maximum. */
  maxWeek: number;
  movingPct: number;
  /** The reader's own open work: blocked + in progress + to do. */
  activeCount: number;
  blockedCount: number;
  unreadCount: number;
  doneThisWeek: number;
  /** Everything finished, not just this week — what the "Done recently" link opens. */
  doneCount: number;
  /** Opens /focus/done. The celebration card absorbed Home's old Done section. */
  onViewDone: () => void;
  /** When the first of the three cards lands; each following card is +200ms. */
  baseDelay: number;
}

/**
 * The three Home insight cards — "your week", as the numbers someone acts on.
 *
 * They are split out of Dashboard because they carry the page's only real
 * choreography: a mount reveal (bars grow from the baseline, the ring draws
 * itself), an idle loop that keeps them alive without asking for attention,
 * and a hover performance where the card shows its data once and settles. The
 * timing constants above are the whole schedule; the keyframes themselves live
 * with the rest of the motion system in `src/index.css`.
 *
 * Every delay is passed down as a custom property rather than written as an
 * :nth-child rule, because Home's panel ladder closes up when a panel above is
 * absent — the cards do not know their own delay until render.
 */
const InsightCards = ({
  weeklyCleared,
  clearedThisWeek,
  bestPastWeek,
  maxWeek,
  movingPct,
  activeCount,
  blockedCount,
  unreadCount,
  doneThisWeek,
  doneCount,
  onViewDone,
  baseDelay,
}: InsightCardsProps) => {
  const cardDelay = (index: number) => baseDelay + index * PANEL_STEP_MS;
  const barMountBase = cardDelay(0) + CONTENT_AFTER_PANEL_MS;
  const ringMount = cardDelay(1) + CONTENT_AFTER_PANEL_MS;

  const sevenWeekTotal = weeklyCleared.reduce((sum, n) => sum + n, 0);
  const movingCount = Math.max(0, activeCount - blockedCount);

  return (
    /* `contents`, not a grid of its own: the three cards are laid out by the
       page's grid, one card per track, so that the Catch Up rail above the
       third card is exactly as wide as the card under it. A nested grid could
       only ever approximate that — two grids with their own gaps line up by
       coincidence, and stop lining up the moment either one is touched. Home
       owns the tracks; this component owns what goes in them. */
    <div className="contents">
      {/* ---- cleared per week ----
          The detail that used to live in an anchored `.insight-tip` popup above
          the card is now a cursor tip: it follows the mouse, which is where the
          reader is already looking (see CursorTip). */}
      <div className="panel-in" style={{ "--panel-delay": `${cardDelay(0)}ms` } as CSSProperties}>
        <Card
          className="insight-card relative h-full border-border p-3"
          {...cursorTip(`· best ${bestPastWeek} · ${sevenWeekTotal} in 7 weeks`, `${clearedThisWeek} this week`)}
        >
          <p className="text-[11.5px] font-extrabold uppercase tracking-wide text-muted-foreground/80">
            Cleared per week
          </p>
          <p className="mt-1 text-[22px] font-extrabold tracking-tight">
            {clearedThisWeek}{" "}
            {clearedThisWeek >= bestPastWeek && clearedThisWeek > 0 && (
              <span className="text-xs font-bold text-green-600 dark:text-green-400">▲ best in 7 weeks</span>
            )}
          </p>
          <div className="mt-2 flex h-11 items-end gap-1.5">
            {weeklyCleared.map((n, i) => {
              const mount = barMountBase + i * BAR_STEP_MS;
              return (
                <div
                  key={i}
                  className="chart-bar flex-1"
                  style={
                    {
                      height: `${Math.max(8, (n / maxWeek) * 100)}%`,
                      "--bar-mount": `${mount}ms`,
                      "--bar-idle": `${mount + BAR_GROW_MS + BAR_IDLE_SWAY_MS[i]}ms`,
                      "--bar-wave": `${i * BAR_STEP_MS}ms`,
                    } as CSSProperties
                  }
                >
                  <b className="chart-bar__value text-[10px] font-extrabold text-[hsl(var(--ring-strong))]">{n}</b>
                  <span
                    className={cn(
                      "chart-bar__fill rounded-t",
                      // The past weeks' bars are a tint of the text colour, not
                      // `--muted`: --muted is one lightness step off the dark
                      // card, so the history read as six empty slots there. Same
                      // reasoning (and the same 18%) as the meter track in
                      // index.css — a muted-foreground tint is a real shape on
                      // white and on near-black alike.
                      i === weeklyCleared.length - 1
                        ? "chart-bar__fill--hot bg-[hsl(var(--ring))]"
                        : "bg-[hsl(var(--muted-foreground)/0.18)]",
                    )}
                  />
                </div>
              );
            })}
          </div>
        </Card>
      </div>

      {/* ---- queue health ---- */}
      <div className="panel-in" style={{ "--panel-delay": `${cardDelay(1)}ms` } as CSSProperties}>
        <Card
          className="insight-card relative h-full border-border p-3"
          {...(activeCount === 0
            ? cursorTip("in your queue", "Nothing active")
            : cursorTip(`· ${blockedCount} blocked · of ${activeCount} active`, `${movingCount} moving`))}
        >
          <p className="text-[11.5px] font-extrabold uppercase tracking-wide text-muted-foreground/80">Queue health</p>
          <div className="mt-1.5 flex items-center gap-3.5">
            {/* `.ring-dial` scopes the arc's colour change to the dial itself,
                the way a bar in the chart card only lights up under the cursor.
                The card-level hover still drives the spin, the % pop and the
                info popup — those are the card performing, not a read-out of
                where the cursor is. */}
            <div className="ring-dial relative h-16 w-16 shrink-0">
              <svg width="64" height="64" viewBox="0 0 72 72" className="block">
                <circle cx="36" cy="36" r="30" fill="none" stroke="hsl(var(--muted))" strokeWidth="9" />
                {/* The rotor carries the hover spin, so the arc's own baked-in
                    -90° start rotation is never fought by the animation. */}
                <g className="ring-rotor">
                  <circle
                    className="ring-arc"
                    cx="36"
                    cy="36"
                    r="30"
                    fill="none"
                    stroke="hsl(var(--ring))"
                    strokeWidth="9"
                    strokeLinecap="round"
                    strokeDasharray={RING_LENGTH}
                    strokeDashoffset={RING_LENGTH - (RING_LENGTH * movingPct) / 100}
                    transform="rotate(-90 36 36)"
                    style={{ "--ring-mount": `${ringMount}ms` } as CSSProperties}
                  />
                </g>
              </svg>
              <div aria-hidden className="ring-glint" />
            </div>
            <div>
              <p className="text-[22px] font-extrabold tracking-tight">
                <span className="ring-pct">{movingPct}%</span>
              </p>
              <p className="text-xs font-medium text-muted-foreground">
                of your queue is moving
                {blockedCount > 0 && ` — ${blockedCount} ticket${blockedCount === 1 ? "" : "s"} waiting on you`}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* ---- celebration ----
          Also the home of finished work. Home used to carry a folded "Done
          recently" section as well as this card, which is the same feel-good
          beat twice; the section went, and this card kept its message and
          gained the way through to the full list. */}
      <div className="panel-in" style={{ "--panel-delay": `${cardDelay(2)}ms` } as CSSProperties}>
        <Card className="insight-card relative h-full border-t-[3px] border-border border-t-green-600 p-3">
          <div className="star-box flex h-8 w-8 items-center justify-center rounded-lg bg-green-600/15 text-green-600 dark:text-green-400">
            {/* Two nested spans, one animation each: `.star-wink` never stops
                looping and `.star-spin` only ever runs on hover. Collapsing
                them into one element is what desynced the wiggle from the
                tile's halo — see the TRAP by `.star-spin` in index.css. */}
            <span className="star-wink">
              <span className="star-spin">
                <Star className="h-4 w-4" />
              </span>
            </span>
          </div>
          <span aria-hidden className="confetti">
            {CONFETTI.map((piece) => (
              <i
                key={piece.tone + piece.delay}
                className={piece.tone}
                style={
                  {
                    "--dx": piece.dx,
                    "--dy": piece.dy,
                    "--confetti-delay": piece.delay,
                  } as CSSProperties
                }
              />
            ))}
          </span>
          {unreadCount === 0 ? (
            <>
              <p className="mt-2 text-[14.5px] font-bold leading-snug">Inbox zero on mentions</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Replies within a day keep tickets moving — the team notices.
              </p>
            </>
          ) : (
            <>
              <p className="mt-2 text-[14.5px] font-bold leading-snug">
                {doneThisWeek} cleared this week
                {doneThisWeek >= bestPastWeek && doneThisWeek > 0 && " — your best yet"}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                Done work tucks itself away; this page stays about what&apos;s next.
              </p>
            </>
          )}
          {doneCount > 0 && (
            <button type="button" onClick={onViewDone} className="textlink mt-1.5 text-xs">
              Done recently <ArrowRight className="ux-arr h-3.5 w-3.5" />
            </button>
          )}
        </Card>
      </div>
    </div>
  );
};

export default InsightCards;
